import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  CalendarDays,
  Camera,
  ChevronLeft,
  ChevronRight,
  Plus,
  Scissors,
  Settings2,
  Sparkles,
} from 'lucide-react'
import {
  useGetCalendarEventsQuery,
  useGetMilestoneEventsQuery,
  useGetCollaborationCalendarEventsQuery,
  useGetPlannedPostsQuery,
  type PlannedPost,
} from '@/redux/collaborationsApi'
import { PostPlanner } from '@/components/calendar/PostPlanner'
import { ContentPieceSheet } from '@/components/calendar/ContentPieceSheet'
import { ContentAssistantSheet } from '@/components/calendar/ContentAssistantSheet'
import { IdeaBank } from '@/components/calendar/IdeaBank'
import { SuggestedPosts } from '@/components/calendar/SuggestedPosts'
import { AddPostSheet, PostingGoalStrip } from '@/components/calendar/CalendarPosts'
import { localToday, mondayOf } from '@/components/calendar/postHelpers'
import PlatformIcon from '@/components/PlatformIcon'
import { cn } from '@/lib/utils'
import { useAppDispatch } from '@/hooks/useRedux'
import { fetchCurrentUser } from '@/redux/userSlice'
import { Button } from '@/components/ui/button'
import { BUTTON_STYLES } from '@/lib/buttonStyles'

// Event type color coding
const EVENT_COLORS: Record<
  string,
  {
    bg: string
    border: string
    dot: string
    text: string
    label: string
    icon: string
    gradient: string
  }
> = {
  nil: {
    bg: 'rgba(245,158,11,0.12)',
    border: 'rgba(245,158,11,0.3)',
    dot: '#f59e0b',
    text: '#B45309',
    label: 'NIL Deadline',
    icon: '⚡',
    gradient: 'from-[#fe9a00] to-[#f54900]',
  },
  post: {
    bg: 'rgba(6,182,212,0.12)',
    border: 'rgba(6,182,212,0.3)',
    dot: '#06b6d4',
    text: '#0E7490',
    label: 'Post Reminder',
    icon: '📱',
    gradient: 'from-[#00b8db] to-[#155dfc]',
  },
  approval: {
    bg: 'rgba(192,253,114,0.1)',
    border: 'rgba(192,253,114,0.25)',
    dot: '#9AE600',
    text: '#3F7A0A',
    label: 'Creative Approval',
    icon: '✓',
    gradient: 'from-[#9ae600] to-[#05df72]',
  },
  organic: {
    bg: 'rgba(236,72,153,0.12)',
    border: 'rgba(236,72,153,0.3)',
    dot: '#EC4899',
    text: '#BE185D',
    label: 'Organic Post',
    icon: '✦',
    gradient: 'from-[#F472B6] to-[#E11D74]',
  },
  shoot: {
    bg: 'rgba(13,148,136,0.12)',
    border: 'rgba(13,148,136,0.3)',
    dot: '#0D9488',
    text: '#0F766E',
    label: 'Shoot day',
    icon: '🎬',
    gradient: 'from-[#2DD4BF] to-[#0F766E]',
  },
  edit: {
    bg: 'rgba(37,99,235,0.12)',
    border: 'rgba(37,99,235,0.3)',
    dot: '#2563EB',
    text: '#1D4ED8',
    label: 'Edit day',
    icon: '✂️',
    gradient: 'from-[#60A5FA] to-[#1D4ED8]',
  },
  collab_event: {
    bg: 'rgba(139,92,246,0.12)',
    border: 'rgba(139,92,246,0.30)',
    dot: '#8b5cf6',
    text: '#6D28D9',
    label: 'Collab Event',
    icon: '📅',
    gradient: 'from-[#8b5cf6] to-[#6d28d9]',
  },
}

const PLATFORM_NAMES: Record<string, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
}
/** "18:30" → "6:30 PM" */
const timeLabel = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number)
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate()
}

function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay()
}

function formatDate(year: number, month: number, day: number) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function formatEventTimeLabel(event: any) {
  if (event.allDay) {
    return 'All day'
  }

  const start = event.start ? new Date(event.start) : null

  if (!start || Number.isNaN(start.getTime())) {
    return ''
  }

  return start.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
}

function formatEventDuration(event: any) {
  if (event.allDay || !event.start || !event.end) {
    return ''
  }

  const start = new Date(event.start)
  const end = new Date(event.end)

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return ''
  }

  const diffMinutes = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000))

  if (diffMinutes < 60) {
    return `${diffMinutes}m`
  }

  const hours = Math.floor(diffMinutes / 60)
  const minutes = diffMinutes % 60

  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`
}

function getEventDateKey(event: any) {
  if (event.date) {
    return event.date
  }

  if (event.start) {
    return String(event.start).slice(0, 10)
  }

  return ''
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#6a7282]">{children}</p>
  )
}

function DarkCard({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={`rounded-2xl border border-border bg-card shadow-[0_20px_25px_rgba(0,0,0,0.1),0_8px_10px_rgba(0,0,0,0.1)] ${className}`}
    >
      {children}
    </div>
  )
}

export default function CalendarView() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const today = new Date()

  // Derive year/month from URL params, defaulting to today
  const year = Number(searchParams.get('year')) || today.getFullYear()
  const month = Number(searchParams.get('month') ?? today.getMonth() + 1) - 1

  const [selectedDate, setSelectedDate] = useState(
    formatDate(today.getFullYear(), today.getMonth(), today.getDate()),
  )

  // ?plan=1 (from the "Posts this week" card) opens straight into planning a post for today
  const [openPiece, setOpenPiece] = useState<PlannedPost | null>(null)
  const [assistantOpen, setAssistantOpen] = useState(false)
  const [addFor, setAddFor] = useState<string | null>(() =>
    searchParams.get('plan') ? localToday() : null,
  )
  // ?review=suggestions (from the "suggested posts" notification) opens the review
  const [reviewOnLoad] = useState(() => searchParams.get('review') === 'suggestions')
  useEffect(() => {
    if (!searchParams.get('review')) return
    const next = new URLSearchParams(searchParams)
    next.delete('review')
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    if (!searchParams.get('plan')) return
    const next = new URLSearchParams(searchParams)
    next.delete('plan')
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // Posting moves the score, so refresh the athlete after a change
  const refreshScore = () => dispatch(fetchCurrentUser())

  const daysInMonth = getDaysInMonth(year, month)
  const firstDay = getFirstDayOfMonth(year, month)

  const monthParams = useMemo(
    () => ({
      start: new Date(Date.UTC(year, month, 1)).toISOString(),
      end: new Date(Date.UTC(year, month + 1, 1)).toISOString(),
    }),
    [year, month],
  )

  const {
    data: calendarData,
    isLoading: calendarLoading,
    isError: calendarError,
  } = useGetCalendarEventsQuery(monthParams)

  const { data: milestoneData, isLoading: milestoneLoading } =
    useGetMilestoneEventsQuery(monthParams)

  const { data: collabEventData } = useGetCollaborationCalendarEventsQuery(monthParams)
  const { data: plannedData } = useGetPlannedPostsQuery({
    from: monthParams.start.slice(0, 10),
    to: new Date(Date.UTC(year, month + 1, 0)).toISOString().slice(0, 10),
  })

  const loading = calendarLoading || milestoneLoading
  const loadError = calendarError ? 'Failed to load Google Calendar events' : null
  const googleCalendarConnected = calendarData?.connected !== false

  function setViewMonth(newYear: number, newMonth: number) {
    setSearchParams({ year: String(newYear), month: String(newMonth + 1) })
  }

  function prevMonth() {
    if (month === 0) setViewMonth(year - 1, 11)
    else setViewMonth(year, month - 1)
  }

  function nextMonth() {
    if (month === 11) setViewMonth(year + 1, 0)
    else setViewMonth(year, month + 1)
  }

  const combinedEvents = useMemo(() => {
    const googleEvents = calendarData?.events ?? []
    const milestoneEvents = milestoneData?.events ?? []
    const mappedGoogleEvents = googleEvents.map((event) => ({
      ...event,
      date: getEventDateKey(event),
      time: formatEventTimeLabel(event),
      duration: formatEventDuration(event),
      campaign: null,
    }))

    const collabEvents = collabEventData?.events ?? []
    const organicPosts = (plannedData?.posts ?? []).map((p) => ({
      id: p.id,
      type: 'organic',
      date: p.status === 'posted' ? (p.postedOn ?? p.plannedOn) : p.plannedOn,
      platform: p.platform,
      title: `${PLATFORM_NAMES[p.platform] ?? 'Post'}: ${p.theme?.label ?? p.idea ?? 'Post'}`,
      time: p.plannedTime ? timeLabel(p.plannedTime) : '',
      detail:
        p.status === 'posted'
          ? 'Posted'
          : p.plannedBy.role === 'manager'
            ? 'Planned by your manager'
            : p.plannedBy.role === 'creator_lab'
              ? 'Planned by the Creator Lab'
              : 'Organic content',
      campaign: null,
      piece: p,
    }))
    // The shoot and edit days of posts still to go up
    const production = (plannedData?.posts ?? []).flatMap((p) =>
      p.status === 'posted'
        ? []
        : [
            ...(p.shootOn
              ? [
                  {
                    id: `shoot-${p.id}`,
                    type: 'shoot',
                    date: p.shootOn,
                    title: `Shoot: ${p.idea ?? p.theme?.label ?? 'post'}`,
                    time: p.shootTime ? timeLabel(p.shootTime) : '',
                    detail: p.shotAt ? 'Filmed' : (p.location ?? 'Shot list ready'),
                    campaign: null,
                    piece: p,
                  },
                ]
              : []),
            ...(p.editOn
              ? [
                  {
                    id: `edit-${p.id}`,
                    type: 'edit',
                    date: p.editOn,
                    title: `Edit: ${p.idea ?? p.theme?.label ?? 'post'}`,
                    time: '',
                    detail: p.editedAt ? 'Edited' : 'Cut, caption, cover',
                    campaign: null,
                    piece: p,
                  },
                ]
              : []),
          ],
    )
    return [
      ...milestoneEvents,
      ...mappedGoogleEvents,
      ...collabEvents,
      ...organicPosts,
      ...production,
    ]
  }, [calendarData, milestoneData, collabEventData, plannedData])

  const eventsForSelected = combinedEvents.filter((e) => e.date === selectedDate)
  const upcomingDeadlines = combinedEvents
    .filter((e) => e.type === 'nil' || e.type === 'approval')
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 3)

  const isToday = (d: number) =>
    today.getFullYear() === year && today.getMonth() === month && today.getDate() === d

  const isSelected = (d: number) => selectedDate === formatDate(year, month, d)

  const plannable = selectedDate >= localToday()
  const showWeek = () =>
    document.getElementById('your-week')?.scrollIntoView({ behavior: 'smooth', block: 'start' })

  return (
    <div className="mx-auto flex w-full max-w-lg flex-col gap-5 pb-10 pt-1">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <h1 className="text-2xl font-bold text-foreground">Content calendar</h1>
          <p className="text-sm text-muted-foreground sm:text-base">
            Your schedule + NIL + organic posts
          </p>
        </div>
        <Button
          onClick={() => navigate('/calendar-setup')}
          className={`h-10 shrink-0 rounded-[14px] px-4 text-sm font-medium ${BUTTON_STYLES.secondary}`}
        >
          <Settings2 className="h-4 w-4" />
          Manage
        </Button>
      </div>

      <div className="flex items-start gap-3 rounded-2xl border border-[#C7E2FB] bg-[#F3F9FF] px-4 py-3.5">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[#1D72D8]" />
        <p className="text-sm leading-5 text-[#334155]">
          Plan your NIL deliverables and organic content in one place. Spot conflicts early, stay
          consistent, and keep your content on track.
        </p>
      </div>

      <PostingGoalStrip />

      <SuggestedPosts openOnLoad={reviewOnLoad} onChanged={refreshScore} />

      {/* The fastest way to a full plan: chat, then add */}
      <button
        type="button"
        onClick={() => setAssistantOpen(true)}
        className="flex w-full items-center gap-3 rounded-2xl bg-[#0F172A] px-4 py-3.5 text-left text-[#FFFFFF] shadow-sm"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#C0FD72] text-[#0F172A]">
          <Sparkles className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-bold">Plan with AI</span>
          <span className="block text-sm text-[#CBD5E1]">
            Ideas, shot lists, and shoot, edit and post days, planned for you
          </span>
        </span>
        <ChevronRight className="h-5 w-5 shrink-0 text-[#CBD5E1]" />
      </button>

      <IdeaBank onOpen={setOpenPiece} onChanged={refreshScore} />

      {loadError ? (
        <div className="rounded-2xl border border-amber-700 bg-amber-950 px-4 py-3 text-sm text-amber-200">
          {loadError}
        </div>
      ) : null}

      <DarkCard className="p-4 sm:p-5">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <SectionLabel>Month view</SectionLabel>
            <div className="mt-1 flex items-center gap-1">
              <button
                type="button"
                aria-label="Previous month"
                onClick={prevMonth}
                className="-ml-1.5 flex h-8 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <p className="whitespace-nowrap text-lg font-bold text-foreground">
                {MONTHS[month].slice(0, 3)} {year}
              </p>
              <button
                type="button"
                aria-label="Next month"
                onClick={nextMonth}
                className="flex h-8 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              {loading ? (
                <span className="ml-1 h-3.5 w-3.5 animate-spin rounded-full border-2 border-muted border-t-muted-foreground" />
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setAddFor(plannable ? selectedDate : localToday())}
            className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full bg-[#E8F9D2] px-4 text-sm font-bold text-[#0F172A] hover:bg-[#D9F5B5]"
          >
            <Plus className="h-4 w-4" /> Add post
          </button>
        </div>

        <div className="mb-4 flex flex-wrap gap-2">
          {Object.entries(EVENT_COLORS).map(([key, val]) => (
            <div
              key={key}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1"
            >
              <div className="h-2 w-2 rounded-full" style={{ background: val.dot }} />
              <span className="text-xs text-muted-foreground">{val.label}</span>
            </div>
          ))}
        </div>

        <div className="overflow-hidden rounded-2xl border border-border bg-card">
          <div className="grid grid-cols-7 border-b border-border">
            {DAYS.map((d) => (
              <div
                key={d}
                className="py-2.5 text-center text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground"
              >
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 px-1 py-1">
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`empty-${i}`} className="min-h-14" />
            ))}

            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1
              const dateStr = formatDate(year, month, day)
              const dayEvents = combinedEvents.filter((e) => e.date === dateStr)
              const selected = isSelected(day)
              const todayCell = isToday(day)
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => setSelectedDate(dateStr)}
                  aria-label={`${MONTHS[month]} ${day}, ${dayEvents.length} events`}
                  aria-pressed={selected}
                  className="flex min-h-14 flex-col items-center gap-1 rounded-xl py-1.5"
                >
                  <span
                    className={cn(
                      'flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold tabular-nums',
                      selected
                        ? 'bg-[#C0FD72] text-[#0F172A]'
                        : todayCell
                          ? 'border-2 border-[#C0FD72] text-[#3F7A0A]'
                          : 'text-foreground hover:bg-muted',
                    )}
                  >
                    {day}
                  </span>
                  <span className="flex h-1.5 gap-0.5">
                    {dayEvents.slice(0, 3).map((e) => (
                      <span
                        key={`${e.type}-${e.id}`}
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ background: EVENT_COLORS[e.type]?.dot }}
                      />
                    ))}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      </DarkCard>

      <section className="space-y-3">
        <SectionLabel>Selected day</SectionLabel>
        <DarkCard className="p-4 sm:p-5">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            })}
          </p>
          <p className="mt-1 text-xl font-bold text-foreground">
            {eventsForSelected.length
              ? `${eventsForSelected.length} event${eventsForSelected.length === 1 ? '' : 's'}`
              : 'Nothing planned'}
          </p>

          {eventsForSelected.length === 0 ? (
            <div className="mt-3 rounded-2xl border border-dashed border-border px-4 py-6 text-center">
              <p className="text-sm text-muted-foreground">
                {plannable ? 'An open day. A good one for a post.' : 'No events on this day.'}
              </p>
              {plannable ? (
                <button
                  type="button"
                  onClick={() => setAddFor(selectedDate)}
                  className="mt-3 inline-flex h-10 items-center gap-1.5 rounded-full bg-[#C0FD72] px-4 text-sm font-bold text-[#0F172A]"
                >
                  <Plus className="h-4 w-4" /> Plan a post
                </button>
              ) : null}
            </div>
          ) : (
            <div className="mt-3 space-y-2.5">
              {eventsForSelected.map((event) => {
                const color = EVENT_COLORS[event.type] ?? EVENT_COLORS.post
                const organic = event.type === 'organic'
                const production = event.type === 'shoot' || event.type === 'edit'
                const isLinked = Boolean(event.collaborationId)
                const d = new Date(event.date + 'T12:00:00')
                const open = event.piece
                  ? () => setOpenPiece(event.piece)
                  : isLinked
                    ? () => navigate(`/collaborations/${event.collaborationId}`)
                    : undefined
                const Row: any = open ? 'button' : 'div'
                return (
                  <Row
                    key={`${event.type}-${event.id}`}
                    type={open ? 'button' : undefined}
                    onClick={open}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-3 text-left',
                      open && 'transition-colors hover:bg-muted/50',
                    )}
                  >
                    <span
                      className={`flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-xl bg-linear-to-br text-[#FFFFFF] ${color.gradient}`}
                    >
                      {organic ? (
                        <PlatformIcon platform={event.platform} className="h-5 w-5" />
                      ) : production ? (
                        event.type === 'shoot' ? (
                          <Camera className="h-5 w-5" />
                        ) : (
                          <Scissors className="h-5 w-5" />
                        )
                      ) : (
                        <>
                          <span className="text-xs font-bold uppercase leading-none">
                            {d.toLocaleDateString('en-US', { month: 'short' })}
                          </span>
                          <span className="mt-0.5 text-base font-bold leading-none">
                            {d.getDate()}
                          </span>
                        </>
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-bold text-foreground">
                        {event.title}
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                        {[
                          event.time || (organic || production ? 'Any time' : 'All day'),
                          event.duration,
                          event.calendarSummary,
                          organic || production ? event.detail : event.campaign,
                        ]
                          .filter(Boolean)
                          .join(' • ')}
                      </span>
                    </span>
                    <span
                      className="hidden shrink-0 rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-[0.1em] min-[400px]:inline"
                      style={{ background: color.bg, color: color.text }}
                    >
                      {color.label}
                    </span>
                    {open ? (
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    ) : null}
                  </Row>
                )
              })}
              {plannable ? (
                <button
                  type="button"
                  onClick={() => setAddFor(selectedDate)}
                  className="inline-flex items-center gap-1 pt-1 text-sm font-semibold text-[#3F7A0A]"
                >
                  <Plus className="h-4 w-4" /> Plan a post this day
                </button>
              ) : null}
            </div>
          )}
        </DarkCard>
      </section>

      <section id="your-week" className="scroll-mt-20 space-y-3">
        <SectionLabel>Your week</SectionLabel>
        <PostPlanner
          key={mondayOf(selectedDate)}
          variant="week"
          initialWeek={selectedDate}
          onChanged={refreshScore}
          onOpenPiece={setOpenPiece}
        />
      </section>

      <section className="space-y-4">
        <SectionLabel>Upcoming NIL deadlines</SectionLabel>
        <DarkCard className="p-5">
          <div className="mb-4 flex items-center gap-2 text-sm text-[#99a1af]">
            <CalendarDays className="h-4 w-4 text-[#8ec5ff]" />
            Upcoming milestones that need your attention
          </div>

          <div className="space-y-3">
            {upcomingDeadlines.map((event) => {
              const color = EVENT_COLORS[event.type]
              const d = new Date(event.date + 'T12:00:00')
              const isLinked = Boolean(event.collaborationId)

              return (
                <div
                  key={event.id}
                  onClick={
                    isLinked
                      ? () => navigate(`/collaborations/${event.collaborationId}`)
                      : undefined
                  }
                  className={`flex items-center gap-3 rounded-[16px] border border-[rgba(54,65,83,0.45)] bg-card px-4 py-4 ${isLinked ? 'cursor-pointer transition-colors hover:border-[rgba(54,65,83,0.8)] hover:bg-card' : ''}`}
                >
                  <div
                    className={`flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-[14px] bg-linear-to-br text-foreground ${color.gradient}`}
                  >
                    <span className="text-xs font-semibold uppercase leading-none text-foreground/80">
                      {d.toLocaleDateString('en-US', { month: 'short' })}
                    </span>
                    <span className="mt-1 text-base font-bold leading-none">{d.getDate()}</span>
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-foreground">{event.title}</p>
                    <p className="mt-0.5 text-xs text-[#99a1af]">
                      {event.campaign || color.label}
                    </p>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    <div
                      className="rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-[0.12em]"
                      style={{ background: color.bg, color: color.text }}
                    >
                      {color.label}
                    </div>
                    {isLinked ? <ChevronRight className="h-4 w-4 text-[#6a7282]" /> : null}
                  </div>
                </div>
              )
            })}
          </div>
        </DarkCard>
      </section>

      <AddPostSheet day={addFor} onClose={() => setAddFor(null)} onChanged={refreshScore} />
      <ContentPieceSheet
        piece={openPiece}
        onClose={() => setOpenPiece(null)}
        onChanged={refreshScore}
      />
      <ContentAssistantSheet
        open={assistantOpen}
        onOpenChange={setAssistantOpen}
        onAdded={refreshScore}
      />
    </div>
  )
}
